import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { PageProps } from '@/types/PageProps';
import {
  CompaniesInfiniteSelectField,
  DefaultRouteTabs,
  FloatingFormItem,
} from '@/components/_common';
import { useDirectSaleStatistics } from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import { Col, DatePicker, Empty, Form, Row } from 'antd';
import moment, { Moment } from 'moment';
import DirectSaleStatistics from '@/components/Movements/DirectSale/DirectSaleStatistics';
import DirectSalePrevisionChart from '@/components/Movements/DirectSale/DirectSalePrevisionChart';
import DirectSalePercentageChart from '@/components/Movements/DirectSale/DirectSalePercentageChart';
import DirectSalePrincingChart from '@/components/Movements/DirectSale/DirectSalePricingChart';

interface FormValues {
  companyIds?: number[];
  startDate?: Moment;
  endDate?: Moment;
}

export default function DirectSaleIndicatorsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<FormValues>();
  const formValues = Form.useWatch([], form);

  const { data: statistics } = useDirectSaleStatistics({
    companyIds: formValues?.companyIds,
    startDate: formValues?.startDate
      ? formValues.startDate.startOf('day').format()
      : undefined,
    endDate: formValues?.endDate
      ? formValues.endDate.endOf('day').format()
      : undefined,
  });

  const newRoute = {
    ...route.parent,
    routes: [
      route.parent,
      ...route.parent.routes.filter((route) => route.key === 'indicators'),
    ],
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={newRoute}
        permissions={route.permissions}
        basePath="/app/movements"
      >
        <Form form={form}>
          <Row gutter={24}>
            <Col span={24} md={8} lg={6}>
              <FloatingFormItem name="companyIds" label="Empresa">
                <CompaniesInfiniteSelectField mode="multiple" />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} lg={6}>
              <FloatingFormItem name="startDate" label="Data inicial">
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder=""
                  format="DD/MM/YYYY"
                  disabledDate={(date) =>
                    date.isAfter(formValues?.endDate || moment(), 'day')
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} lg={6}>
              <FloatingFormItem name="endDate" label="Data final">
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder=""
                  format="DD/MM/YYYY"
                  disabledDate={(date) =>
                    date.isBefore(formValues?.startDate, 'day') ||
                    date.isAfter(moment(), 'day')
                  }
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </Form>

        {!statistics && (
          <div
            style={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Empty description="Preenche os filtros para exibir os indicadores" />
          </div>
        )}

        {!!statistics && (
          <>
            <DirectSaleStatistics directSaleStatistics={statistics} />

            <Row style={{ marginTop: 48 }} gutter={[24, 24]}>
              <Col span={24} lg={12} xl={8}>
                <DirectSalePrevisionChart directSaleStatistics={statistics} />
              </Col>

              <Col span={24} lg={12} xl={8}>
                <DirectSalePercentageChart directSaleStatistics={statistics} />
              </Col>

              <Col span={24} lg={12} xl={8}>
                <DirectSalePrincingChart directSaleStatistics={statistics} />
              </Col>
            </Row>
          </>
        )}
      </DefaultRouteTabs>
    </>
  );
}
